import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
} from "react";
import { CssBaseline, ThemeProvider, useMediaQuery } from "@mui/material";
import Cookies from "js-cookie";
import {
  lightTheme,
  darkTheme,
  accentColours,
  getAccentTheme,
} from "./ColourTheme";

// Create the context
const ColourContext = createContext();

const ColourProvider = ({ children }) => {
  const systemPrefersDark = useMediaQuery("(prefers-color-scheme: dark)");

  // Load theme and accent color from cookies or fallback to default
  const getInitialTheme = () =>
    localStorage.getItem("themeMode") || Cookies.get("themeMode") || "system";
  const getInitialAccent = () =>
    localStorage.getItem("accentColour") ||
    Cookies.get("accentColour") ||
    "dodgerBlue";

  const [themeMode, setThemeMode] = useState(getInitialTheme);
  const [accentColour, setAccentColour] = useState(getInitialAccent);

  // Automatically update theme based on system preferences
  const appliedTheme = useMemo(() => {
    if (themeMode === "light")
      return getAccentTheme(lightTheme, accentColours[accentColour]);
    if (themeMode === "dark")
      return getAccentTheme(darkTheme, accentColours[accentColour]);

    // If themeMode is 'system', use system preference (dark/light)
    return systemPrefersDark
      ? getAccentTheme(darkTheme, accentColours[accentColour])
      : getAccentTheme(lightTheme, accentColours[accentColour]);
  }, [themeMode, accentColour, systemPrefersDark]);

  useEffect(() => {
    localStorage.setItem("themeMode", themeMode);
    localStorage.setItem("accentColour", accentColour);
    Cookies.set("themeMode", themeMode, {
      expires: 7,
      sameSite: "Strict",
      secure: true,
    });
    Cookies.set("accentColour", accentColour, {
      expires: 7,
      sameSite: "Strict",
      secure: true,
    });
  }, [themeMode, accentColour]);

  const handleToggleTheme = (mode) => setThemeMode(mode);
  const handleSetAccentColour = (accentColour) => {
    if (accentColours[accentColour]) {
      setAccentColour(accentColour);
      Cookies.set("accentColour", accentColour, {
        expires: 7,
        sameSite: "Strict",
        secure: true,
      });
    }
  };

  // Retrieve saved settings from localStorage first when component mounts
  useEffect(() => {
    const savedThemeMode =
      localStorage.getItem("themeMode") || Cookies.get("themeMode");
    const savedAccentColour =
      localStorage.getItem("accentColour") || Cookies.get("accentColour");

    if (savedThemeMode) setThemeMode(savedThemeMode);
    if (savedAccentColour && accentColours[savedAccentColour]) {
      setAccentColour(savedAccentColour);
    }
  }, []);

  return (
    <ColourContext.Provider
      value={{
        themeMode,
        handleToggleTheme,
        accentColour,
        handleSetAccentColour,
        accentColours,
      }}
    >
      <ThemeProvider theme={appliedTheme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColourContext.Provider>
  );
};

const useColourMode = () => useContext(ColourContext);

export { ColourContext, ColourProvider, useColourMode };
